import { useCallback } from 'react';
import { useNoticeBannersStackContext } from '@sg/garnish';

import type { PaymentMethod } from '@order/graphql';
import { useLocalizationContext } from '@order/Localization';

import {
  useDeleteBillingAccountMutation,
  useDeletePaymentMethodCardMutation,
} from './graphql/DeletePaymentMethod.generated';

export function useDeletePaymentMethod(props: DeletePaymentMethodProps) {
  const { onSuccess } = props;

  const { t } = useLocalizationContext();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();

  const [deleteBillingAccountResponse, deleteBillingAccountMutation] =
    useDeleteBillingAccountMutation();

  const [deletePaymentMethodCardResponse, deletePaymentMethodCardMutation] =
    useDeletePaymentMethodCardMutation();

  const deleteBillingAccount = useCallback(
    async (paymentMethodId: string) => {
      const response = await deleteBillingAccountMutation({
        input: { id: Number(paymentMethodId) },
      });

      const result = response.data?.deleteBillingAccount;
      const isSuccess = result?.__typename === 'DeleteBillingAccountResponse';

      return { isSuccess };
    },
    [deleteBillingAccountMutation],
  );

  const deletePaymentMethodCard = useCallback(
    async (paymentMethodId: string) => {
      const response = await deletePaymentMethodCardMutation({
        input: { id: paymentMethodId },
      });

      const result = response.data?.deletePaymentMethodCard;
      const isSuccess =
        result?.__typename === 'PaymentMethodCard' &&
        result?.id === paymentMethodId;

      return { isSuccess };
    },
    [deletePaymentMethodCardMutation],
  );

  const deletePaymentMethod = useCallback(
    async (paymentMethod: PaymentMethod) => {
      const { isSuccess } =
        paymentMethod.__typename === 'PaymentMethodCard'
          ? await deletePaymentMethodCard(paymentMethod.id)
          : await deleteBillingAccount(paymentMethod.id);

      if (isSuccess) {
        addNoticeBanner({
          text: t('credit-card-form.remove.success'),
          palette: 'success',
        });

        onSuccess();

        return;
      }

      const userError = t('credit-card-form.error.delete');

      addNoticeBanner({ text: userError, palette: 'caution' });
    },
    [
      deleteBillingAccount,
      deletePaymentMethodCard,
      t,
      addNoticeBanner,
      onSuccess,
    ],
  );

  return {
    deletePaymentMethod,
    isDeleting:
      deletePaymentMethodCardResponse.fetching ||
      deleteBillingAccountResponse.fetching,
  };
}

// ─── Types ───────────────────────────────────────────────────────────────────

type DeletePaymentMethodProps = Readonly<{
  onSuccess: () => void;
}>;
